import { ref } from 'vue'
import { defineStore } from 'pinia'
import { authApi } from '../api/auth.api'
import { useAppStore } from '@/modules/app/stores/app.store'
import { AuthState } from '@/modules/users/types/user.enums'
import { ROUTE_NAMES } from '@/router/constants/route.constants'
import { IDENTITY_PROVIDER_TYPES } from '../constants/auth.constants'
import type { RouteLocationRaw } from 'vue-router'
import type { Identity } from '@/modules/users/models/user.model'
import type { HttpResponse } from '@/modules/services/http'
import type { AuthErrorCode } from '../types/auth.enums'
import type { ClaimResponse } from '../models/auth.models'

const AUDIENCE_URL = import.meta.env.VITE_AUTH_AUDIENCE_URL
const CLIENT_ID = import.meta.env.VITE_AUTH_CLIENT_ID
const ISSUER_URL = import.meta.env.VITE_AUTH_ISSUER_URL
const AUTH_PATH = import.meta.env.VITE_AUTH_AUTHORIZE_PATH
const REDIRECT_URI = import.meta.env.VITE_AUTH_REDIRECT_URI
const RETURN_TO_URL = import.meta.env.VITE_AUTH_RETURN_TO_URL
const LOGOUT_PATH = import.meta.env.VITE_AUTH_LOGOUT_PATH
const SCOPE = import.meta.env.VITE_AUTH_SCOPE
const HOST = `${window.location.protocol}//${window.location.host}`

export const useAuthStore = defineStore('auth', () => {
  const appStore = useAppStore()

  const identity = ref<Identity>()

  const getLoginHint = (): string | null => localStorage.getItem('login_hint')

  const setLoginHint = (email: string): void => localStorage.setItem('login_hint', email)

  const removeLoginHint = (): void => localStorage.removeItem('login_hint')

  const isIdentityReady = async (): Promise<boolean> => {
    const { data, error } = await authApi.getIdentity()

    if (error || !data) return false

    identity.value = data

    if (identity.value.state === AuthState.Authenticated) return true

    return false
  }

  const exchangeCode = async (
    code: string,
    state: string,
  ): Promise<HttpResponse<ClaimResponse, AuthErrorCode>> => {
    removeLoginHint()

    const response = await authApi.exchangeCode(code, state, HOST)

    if (!response.error && response.data) {
      const { identityProvider, identityProviderEmail } = response.data

      if (
        identityProviderEmail &&
        identityProvider === IDENTITY_PROVIDER_TYPES.MICROSOFT_ENTRA_ID
      ) {
        setLoginHint(identityProviderEmail)
      }
    }

    return response
  }

  const exchangeSession = async (): Promise<HttpResponse<null, AuthErrorCode>> =>
    await authApi.exchangeSession()

  const tryLogout = async (isRemoveLoginHint?: boolean): Promise<void> => {
    appStore.clearAll()

    if (isRemoveLoginHint) {
      removeLoginHint()
    }

    await authApi.tryLogout()
  }

  const redirectToLogin = (): void => {
    const url = new URL(`${ISSUER_URL}${AUTH_PATH}`)

    url.searchParams.set('response_type', 'code')
    url.searchParams.set('client_id', CLIENT_ID)
    url.searchParams.set('redirect_uri', `${HOST}${REDIRECT_URI}`)
    url.searchParams.set('scope', SCOPE)
    url.searchParams.set('state', crypto.randomUUID())
    url.searchParams.set('audience', AUDIENCE_URL)
    url.searchParams.set('prompt', 'select_account')

    const loginHint = getLoginHint()

    if (loginHint) {
      url.searchParams.set('login_hint', loginHint)
    }

    window.location.href = url.toString()
  }

  const redirectToLogout = (): void => {
    const url = new URL(`${ISSUER_URL}${LOGOUT_PATH}`)

    url.searchParams.set('client_id', CLIENT_ID)
    url.searchParams.set('returnTo', `${HOST}${RETURN_TO_URL}`)

    window.location.href = url.toString()
  }

  const getRouteLocation = (state?: AuthState): RouteLocationRaw | boolean => {
    switch (state) {
      case AuthState.SignIn:
        redirectToLogin()
        return false
      case AuthState.Authenticated:
        return true
      case AuthState.Blocked:
        return { name: ROUTE_NAMES.BLOCKED }
      case AuthState.Activation:
        return { name: ROUTE_NAMES.ACTIVATE }
      case AuthState.Registration:
        return { name: ROUTE_NAMES.REGISTER }
      default:
        return false
    }
  }

  return {
    identity,
    isIdentityReady,
    exchangeCode,
    exchangeSession,
    tryLogout,
    redirectToLogin,
    redirectToLogout,
    getRouteLocation,
  }
})
